<template>
    <div class="p-1">
        <!-- Confirmación: el usuario pierde su rol de inmobiliaria -->
        <div v-if="pendiente">
            <div class="alert alert-warning text-sm mb-3">
                <i class="fas fa-exclamation-triangle me-1"></i>
                <strong>{{ pendiente.usuario.email }}</strong> pertenece hoy a una inmobiliaria con
                {{ pendiente.roles.length > 1 ? 'los roles' : 'el rol' }}
                <strong>{{ pendiente.roles.map((r) => r.nombre).join(', ') }}</strong>.
                Al asignarle <strong>{{ rol.nombre }}</strong> quedará <strong>solo</strong> con este rol
                y perderá {{ pendiente.roles.length > 1 ? 'esos roles' : 'ese rol' }}.
            </div>
            <div class="d-flex gap-2 justify-content-end">
                <soft-button variant="outline" color="secondary" size="sm" @click="pendiente = null">
                    Cancelar
                </soft-button>
                <soft-button
                    variant="gradient"
                    color="warning"
                    size="sm"
                    :loading="asignandoId === pendiente.usuario.id"
                    @click="asignar(pendiente.usuario, true)"
                >
                    Sí, convertir en admin
                </soft-button>
            </div>
        </div>

        <template v-else>
            <label class="form-label fw-semibold text-xs">
                <i class="fas fa-search text-secondary me-1"></i>
                Buscar usuario por correo o nombre de usuario
            </label>
            <div class="d-flex gap-2 mb-2">
                <input
                    v-model="query"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Mínimo 2 caracteres..."
                    @keyup.enter="buscar"
                />
                <soft-button
                    variant="gradient"
                    color="primary"
                    size="sm"
                    :loading="buscando"
                    :disabled="query.trim().length < 2"
                    @click="buscar"
                >
                    <i class="fas fa-search me-1"></i>
                    Buscar
                </soft-button>
            </div>

            <div v-if="errorMsg" class="alert alert-danger py-2 px-3 text-sm">{{ errorMsg }}</div>

            <div class="results-box">
                <div v-if="buscando" class="text-center py-3 text-muted small">
                    <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                    Buscando...
                </div>
                <div v-else-if="!buscado" class="text-center text-muted py-4 small">
                    Escribe un término y presiona Buscar
                </div>
                <div v-else-if="!resultados.length" class="text-center text-muted py-4 small">
                    No se encontraron usuarios
                </div>
                <ul v-else class="list-unstyled mb-0">
                    <li
                        v-for="usuario in resultados"
                        :key="usuario.id"
                        class="result-item d-flex align-items-center justify-content-between gap-2"
                    >
                        <div class="min-w-0">
                            <div class="text-sm fw-semibold text-dark text-truncate">{{ usuario.email }}</div>
                            <div class="text-xxs text-muted">
                                {{ usuario.username }}
                                <span v-if="nombresRoles(usuario)"> · {{ nombresRoles(usuario) }}</span>
                            </div>
                        </div>
                        <span v-if="rolActual(usuario)?.id === rol.id" class="badge bg-light text-dark border">
                            Ya tiene este rol
                        </span>
                        <soft-button
                            v-else
                            variant="gradient"
                            color="primary"
                            size="sm"
                            :loading="asignandoId === usuario.id"
                            @click="asignar(usuario)"
                        >
                            {{ rolActual(usuario) ? 'Mover aquí' : 'Asignar' }}
                        </soft-button>
                    </li>
                </ul>
            </div>

            <div class="d-flex justify-content-end mt-3 pt-3 border-top">
                <soft-button variant="outline" color="secondary" size="sm" @click="$emit('close')">
                    Cerrar
                </soft-button>
            </div>
        </template>
    </div>
</template>

<script>
import { ref } from 'vue';
import { useStore } from 'vuex';
import SoftButton from '@/components/SoftButton.vue';
import { mensajeError } from '@/store/plataformaStore';

export default {
    name: 'AsignarUsuarioModal',
    components: { SoftButton },
    props: {
        rol: { type: Object, required: true },
        // { [userId]: rol de plataforma actual }
        adminsActuales: { type: Object, default: () => ({}) }
    },
    emits: ['close', 'asignado'],
    setup(props, { emit }) {
        const store = useStore();

        const query = ref('');
        const resultados = ref([]);
        const buscando = ref(false);
        const buscado = ref(false);
        const asignandoId = ref(null);
        const errorMsg = ref(null);
        const pendiente = ref(null);

        const buscar = async () => {
            if (query.value.trim().length < 2) return;
            buscando.value = true;
            errorMsg.value = null;
            try {
                resultados.value = await store.dispatch('appPlataforma/buscarUsuariosAction', query.value.trim());
                buscado.value = true;
            } catch (error) {
                errorMsg.value = mensajeError(error, 'Error al buscar usuarios');
            } finally {
                buscando.value = false;
            }
        };

        const rolActual = (usuario) => props.adminsActuales[usuario.id] || null;

        const nombresRoles = (usuario) => (usuario.roles || []).map((r) => r.nombre).join(', ');

        const asignar = async (usuario, confirmar = false) => {
            asignandoId.value = usuario.id;
            errorMsg.value = null;
            try {
                await store.dispatch('appPlataforma/asignarUsuarioAction', {
                    roleId: props.rol.id,
                    userId: usuario.id,
                    confirmar,
                });
                emit('asignado', `${usuario.email} ahora es ${props.rol.nombre}`);
            } catch (error) {
                const body = error?.response?.data || {};
                if (error?.response?.status === 409 && Array.isArray(body.data?.roles_actuales)) {
                    pendiente.value = { usuario, roles: body.data.roles_actuales };
                } else {
                    pendiente.value = null;
                    errorMsg.value = mensajeError(error, 'No se pudo asignar el usuario');
                }
            } finally {
                asignandoId.value = null;
            }
        };

        return {
            query,
            resultados,
            buscando,
            buscado,
            asignandoId,
            errorMsg,
            pendiente,
            buscar,
            rolActual,
            nombresRoles,
            asignar
        };
    }
};
</script>

<style lang="scss" scoped>
.results-box {
    max-height: 340px;
    overflow-y: auto;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    background: #fff;
}

.result-item {
    padding: 0.6rem 0.9rem;
    border-bottom: 1px solid #f0f2f5;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #f8f9fa;
    }
}

.min-w-0 {
    min-width: 0;
}
</style>
